import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { unsubscribeContent } from '../content/unsubscribe';
import { pageSeo } from '../content/seo';
import { Seo } from '../components/seo/Seo';
import { Section } from '../components/layout/Section';
import { requestUnsubscribe } from '../lib/api';

/**
 * `/unsubscribe` (spec section 17).
 *
 * Public, unauthenticated, and noindex — the URL carries a token, so it must
 * never end up in an index (`pageSeo.unsubscribe`).
 *
 * The request fires on load rather than behind a button. The person already
 * expressed intent by clicking the link in the email; asking them to click a
 * second time is the pattern that makes unsubscribing feel like an argument.
 * The endpoint is idempotent, so a reload or a mail-client prefetch changes
 * nothing (src/server/routes/unsubscribe.ts).
 *
 * Three states and no others: working, done, failed. A missing token skips
 * straight to failed without a request — there is nothing to ask about.
 */
type State = 'working' | 'done' | 'error';

export function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>(token === '' ? 'error' : 'working');

  // React 18+ mounts effects twice in development StrictMode. The endpoint is
  // idempotent so a second call would be harmless, but there is no reason to
  // make it.
  const requested = useRef(false);

  useEffect(() => {
    if (token === '' || requested.current) return;
    requested.current = true;

    let active = true;
    void requestUnsubscribe(token).then((result) => {
      if (!active) return;
      setState(result.ok ? 'done' : 'error');
    });

    return () => {
      active = false;
    };
  }, [token]);

  const { headline, body } = readState(state);

  return (
    <>
      <Seo seo={pageSeo.unsubscribe} />
      <Section labelledBy="unsubscribe-heading">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-signal">
          {unsubscribeContent.heading}
        </p>
        <h1
          id="unsubscribe-heading"
          className="mt-3 max-w-3xl text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl"
        >
          {headline}
        </h1>
        {/* Announced for a screen reader: the headline changes after load,
            without a navigation, once the request comes back. */}
        <div aria-live="polite">
          {body.map((paragraph) => (
            <p key={paragraph} className="mt-5 max-w-2xl text-base leading-relaxed text-ink/85">
              {paragraph}
            </p>
          ))}
        </div>
        <p className="mt-8">
          <Link to="/" className="text-ink underline underline-offset-4">
            {unsubscribeContent.backLinkLabel}
          </Link>
        </p>
      </Section>
    </>
  );
}

function readState(state: State): { headline: string; body: readonly string[] } {
  if (state === 'working') {
    return {
      headline: unsubscribeContent.workingHeadline,
      body: [unsubscribeContent.workingBody],
    };
  }
  if (state === 'done') {
    return { headline: unsubscribeContent.doneHeadline, body: unsubscribeContent.doneBody };
  }
  return { headline: unsubscribeContent.errorHeadline, body: unsubscribeContent.errorBody };
}
